import React from 'react';
import { Menu } from '@headlessui/react';
import clsx from 'clsx';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { IconButton } from './Button';

/**
 * DropdownMenu \u2014 canonical action-menu atom.
 *
 * Ships as PT-39a (2026-07-27) to retire the recurring kebab + panel + item
 * pattern that had drifted across ~13 callsites. Wraps Headless UI `Menu` so
 * outside-click, `Escape`, arrow-key navigation, and focus management come
 * for free. Uses the app's semantic tokens (`--color-surface`,
 * `--color-border`, `--color-error`, `--color-error-bg`, `--radius-md`) so
 * there is no raw hex leaking into consumers.
 *
 * API (declarative form covers ~all callsites):
 *
 *   <DropdownMenu
 *     items={[
 *       { label: 'View',    onClick: () => navigate(\`/quotes/\${id}\`) },
 *       { label: 'Edit',    onClick: onEdit, icon: <PencilIcon className="h-4 w-4" /> },
 *       { divider: true },
 *       { label: 'Duplicate', disabled: true, title: 'Coming soon' },
 *       { label: 'Delete',  onClick: onDelete, variant: 'danger' },
 *     ]}
 *     ariaLabel="Row actions"
 *     align="end"                        // 'start' | 'end' (default 'end')
 *     stopPropagation                     // true when embedded in a row-click TableRow
 *   />
 *
 * Custom trigger for text/CTA menus (JobDetail 'Create \u25be', Dashboard '+ Create'):
 *
 *   <DropdownMenu
 *     items={createOptions}
 *     trigger={<Button variant="secondary"><PlusIcon /> Create <ChevronDownIcon /></Button>}
 *   />
 *
 * Item shape (all fields optional except `label` for non-divider entries):
 *   {
 *     label:    string,                   // shown text
 *     onClick:  () => void,               // fires on click; menu closes after
 *     icon:     ReactNode,                // rendered inline before label
 *     variant:  'default' | 'danger',    // 'danger' \u2192 red text + red-tinted hover
 *     disabled: boolean,                  // greys out, prevents click
 *     title:    string,                   // native tooltip (paired with `disabled` for "Coming soon")
 *     hidden:   boolean,                  // omit entirely from DOM
 *     divider:  true,                     // renders a separator line; label ignored
 *     key:      string,                   // stable react key; falls back to index
 *   }
 *
 * Design decisions locked from cross-callsite reconciliation (2026-07-27):
 *   \u2022 Active bg = gray-50 (Quotes + Dashboard); overrides JobDetail's gray-100.
 *   \u2022 Panel bg = var(--color-surface); overrides bg-white in older callers.
 *   \u2022 Destructive tokens: var(--color-error) text on default, var(--color-error-bg) on hover.
 *   \u2022 `stopPropagation` prop lifts every consumer's `onClick={(e) => e.stopPropagation()}`
 *     boilerplate into the atom \u2014 default is false so header/CTA menus aren't affected.
 */
const DropdownMenu = React.forwardRef(function DropdownMenu(
  {
    items = [],
    trigger,
    ariaLabel = 'Actions',
    align = 'end',
    stopPropagation = false,
    className,
    panelClassName,
    ...rest
  },
  ref,
) {
  // Default trigger = kebab. Explicit `trigger` overrides.
  const renderTrigger = trigger != null
    ? (
      <Menu.Button as={React.Fragment}>
        {trigger}
      </Menu.Button>
    )
    : (
      <Menu.Button
        as={IconButton}
        aria-label={ariaLabel}
        aria-haspopup="menu"
      >
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
    );

  // Optional root-level stop-propagation trap. Applied to both the trigger
  // click AND every item click, so callers wrapped in row-click handlers
  // don't have to remember to add it manually.
  const stopIfNeeded = stopPropagation
    ? (e) => e.stopPropagation()
    : undefined;

  // Headless UI's `Menu` doesn't forward arbitrary props like `onClick` onto
  // its rendered element; wrap with a real DOM `<div>` so the stop-propagation
  // trap actually catches trigger + item click bubbling.
  const rootProps = {
    ref,
    className: clsx('relative inline-block text-left', className),
    onClick: stopIfNeeded,
    ...rest,
  };

  return (
    <div {...rootProps}>
      <Menu as="div" className="relative inline-block text-left">
        {renderTrigger}
      <Menu.Items
        anchor={{ to: `bottom ${align}`, gap: 4 }}
        className={clsx(
          // PT-53b M1+M2 (2026-08-03): max-w-[20rem] retained as a fallback for
          // non-anchor contexts (if any); the real enforcement is the
          // `[role="menu"][data-anchor]` !important rule in tokens.css which
          // beats Floating-UI's inline max-width. shadow-sm dropped — the
          // same CSS rule applies --elevation-popover via !important.
          'z-30 py-1 min-w-[12rem] max-w-[20rem] w-max',
          'border focus:outline-none',
          panelClassName,
        )}
        style={{
          backgroundColor: 'var(--color-surface)',
          borderColor: 'var(--color-border)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        {items.map((item, idx) => {
          if (!item || item.hidden) return null;
          if (item.divider || item.separator) {
            return (
              <div
                key={item.key || `divider-${idx}`}
                role="separator"
                className="my-1 border-t"
                style={{ borderColor: 'var(--color-border)' }}
              />
            );
          }
          return (
            <DropdownMenuItem
              key={item.key || `item-${idx}`}
              icon={item.icon}
              variant={item.variant}
              disabled={item.disabled}
              title={item.title}
              onClick={item.onClick}
              stopPropagation={stopPropagation}
              className={item.className}
            >
              {item.label}
            </DropdownMenuItem>
          );
        })}
      </Menu.Items>
      </Menu>
    </div>
  );
});

/**
 * DropdownMenuItem \u2014 compound sub-component for consumers that need control
 * over item internals (e.g. custom nodes inside the item). Most callers
 * should use the declarative `items` prop on `<DropdownMenu>` instead.
 */
export function DropdownMenuItem({
  children,
  icon,
  variant = 'default',
  disabled = false,
  title,
  onClick,
  stopPropagation = false,
  className,
}) {
  const isDanger = variant === 'danger' && !disabled;
  return (
    <Menu.Item disabled={disabled}>
      {({ active }) => (
        <button
          type="button"
          role="menuitem"
          disabled={disabled}
          title={title}
          data-variant={isDanger ? 'danger' : 'default'}
          data-disabled={disabled ? 'true' : 'false'}
          onClick={(e) => {
            if (stopPropagation) e.stopPropagation();
            if (disabled) return;
            onClick?.(e);
          }}
          className={clsx(
            'w-full text-left px-3 py-2 text-sm inline-flex items-center gap-2',
            'focus:outline-none',
            disabled && 'cursor-not-allowed',
            className,
          )}
          style={{
            color: disabled
              ? 'var(--color-text-subtle)'
              : isDanger
                ? 'var(--color-error)'
                : 'var(--color-text-default)',
            backgroundColor: active && !disabled
              ? (isDanger ? 'var(--color-error-bg)' : 'var(--color-surface-sunken)')
              : 'transparent',
          }}
        >
          {icon != null && (
            <span className="inline-flex shrink-0" aria-hidden="true">
              {icon}
            </span>
          )}
          <span className="flex-1">{children}</span>
        </button>
      )}
    </Menu.Item>
  );
}

/**
 * DropdownMenuSeparator \u2014 compound sub-component for explicit divider control.
 * Declarative form: `{ divider: true }` in the items array.
 */
export function DropdownMenuSeparator() {
  return (
    <div
      role="separator"
      className="my-1 border-t"
      style={{ borderColor: 'var(--color-border)' }}
    />
  );
}

export default DropdownMenu;
